import {Component, computed, inject, input, resource} from "@angular/core";
import {SimCompetenceCategoryService, ThemeService} from "@services";
import {EChartsCoreOption} from "echarts/core";
import {AssessmentContextService} from "../services/assessment-context.service";
import {AssessmentAnswerService} from "../services/assessment-answer.service";
import {CompetenceCategory} from "@models";

@Component({
	selector: "sim-competence-category-answer",
	templateUrl: "./competence-category-answer.component.html",
	styleUrl: "./competence-category-answer.component.scss",
	standalone: false,
})
export class CompetenceCategoryAnswerComponent {
	// Injections
	readonly #themeService = inject(ThemeService);
	readonly #contextService = inject(AssessmentContextService);
	readonly #competenceCategoryService = inject(SimCompetenceCategoryService);
	readonly #answerService = inject(AssessmentAnswerService);

	// Inputs
	assessmentItemId = input.required<string>();

	competenceCategoryResource = resource({
		loader: (_) => this.#competenceCategoryService.getCompetenceCategories(),
	});

	// Signals
	readonly #form = computed(() => this.#contextService.findItemControl(this.assessmentItemId()).controls.competenceCategoryDistribution);
	readonly #competenceCategories = computed(
		() => this.#answerService.cachedCompetencyCategories() ?? this.competenceCategoryResource.value() ?? [],
	);
	protected containerHeight = computed(() => {
		const categoryAmount = this.#form().value.length;
		return Math.max(100, 100 + (categoryAmount - 3) * 30);
	});
	protected chartData = computed(() => this.computeChartData(this.#competenceCategories(), this.#form(), this.containerHeight()));

	// Properties
	public commonChartOptions: EChartsCoreOption = {
		title: {
			text: "Competencies",
			textStyle: {
				color: this.#themeService.isDarkTheme() ? "white" : "black",
			},
		},
		legend: {
			orient: "vertical",
			bottom: 0,
			left: 0,
			selectedMode: false,
			textStyle: {
				color: this.#themeService.isDarkTheme() ? "white" : "black",
			},
		},
		series: [],
	};

	private computeChartData(
		categories: CompetenceCategory[],
		form: ReturnType<AssessmentContextService["findItemControl"]>["controls"]["competenceCategoryDistribution"],
		containerHeight: number,
	): EChartsCoreOption {
		if (categories.length === 0) {
			return {};
		}
		const radius = containerHeight / 3;
		return {
			series: [
				{
					type: "pie",
					radius,
					left: "right",
					right: -2.5 * radius,
					label: {
						show: false,
					},
					data: form.value.map((distributionEntry) => {
						const category = categories.find((category) => category.id === distributionEntry.competenceCategoryId);
						return {
							value: distributionEntry.percentage,
							name: category?.name ?? "N/A",
							itemStyle: {
								color: category?.color ?? "grey",
							},
						};
					}),
				},
			],
		};
	}
}
